import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate } from "react-router";
import { createColumnHelper, ColumnDef, Row } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStars,
  faShieldCheck,
  faTelescope,
  faEye,
  faTrash,
} from "@fortawesome/pro-light-svg-icons";
import { AddToPersonaGroupDialog } from "./components/personaGroups/index.ts";
import { fetchPersonaGroups } from "../../stores/thunks/personaGroupsThunk.tsx";
import {
  faPlus,
  faStars as solidFaStars,
} from "@fortawesome/pro-solid-svg-icons";
import { fullFormatNumbers } from "../../helpers/functions/formatNumber";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks";
import {
  fetchPersonasData,
  deletePersonaThunk,
} from "../../stores/thunks/personasThunk";
import { setIsLoadingGenerate } from "../../stores/slice/personasSlice.tsx";
import { GeneratePersonaDialog } from "../../components/layout/components/globalSlideouts/components/persona/components/generatePersonaDialog/GeneratePersonaDialog.tsx";
import personaEmpty from "/images/empty_states/persona-empty.svg";
import { getPlatformIcon } from "../../helpers/functions/getPlatformIcon.tsx";
import { personasListTabs } from "../../components/layout/components/globalSlideouts/components/persona/components/data/personaFormData.ts";
import { FilterMapType } from "../../components/layout/components/globalSlideouts/components/persona/components/data/personaType.ts";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate.tsx";
import {
  RevActionsHeader,
  RevAlert,
  RevAvatar,
  RevButton,
  RevCheckbox,
  RevDialog,
  type RevDialogFooterBtnListType,
  RevEmptyPage,
  RevIconButton,
  RevLink,
  RevSpinner,
  RevStatusBadge,
  RevTable,
  RevTabs,
  RevTruncatedTextWithTooltip,
  RevTooltip,
} from "@revmatics/catalyst";
import { pollForPersonaGeneration } from "../../helpers/services/pollingService.ts";
import { displayAlert } from "../../stores/slice/alertSlice.tsx";
import { isPersonaEnriching } from "../../helpers/functions/isPersonaEnriching.ts";
import { PersonaCommunity } from "../../stores/types/personasTypes.tsx";
import { getFitColor, getFitName } from "../../helpers/functions/getFitName.ts";

interface Platform {
  id: number;
  name: string;
  icon_class: string;
}

export interface PersonaData {
  id: number;
  name: string;
  persona_picture?: string | null;
  persona_picture_url?: string | null;
  gender?: string;
  occupation?: string;
  created_at?: string;
  updated_at?: string;
  affinity_strength?: string | number | null;
  top_platforms?: Platform[];
  communities?: PersonaCommunity[];
  interests?: string[];
  total_size?: number;
  is_ai_generated: boolean;
  is_platform: boolean;
  status: string;
}

const columnHelper = createColumnHelper<PersonaData>();

const PersonaNameCell = ({ row }: { row: Row<PersonaData> }) => {
  const { personaDetails: ffPersonaDetails } = useFlags();
  const {
    name,
    persona_picture,
    persona_picture_url,
    gender,
    id,
    is_ai_generated,
    is_platform,
    status,
  } = row.original;
  const imageUrl = persona_picture || persona_picture_url;
  const [showFallbackIcon, setShowFallbackIcon] = useState(!imageUrl);

  useEffect(() => {
    setShowFallbackIcon(!imageUrl);
  }, [imageUrl]);

  const handleImageError = () => {
    setShowFallbackIcon(true);
  };

  return (
    <div className="flex items-center gap-3">
      {showFallbackIcon ? (
        <RevAvatar
          variant="circle"
          size="medium"
          theme="light"
          firstName={name.split(" ")[0]}
          lastName={name.split(" ").pop()}
          gender={gender}
        />
      ) : (
        <div className="h-10 w-10 border border-monochrome-100 rounded-full overflow-hidden">
          <img
            src={imageUrl || ""}
            alt={name}
            className="w-full h-full object-cover flex-shrink-0"
            onError={handleImageError}
          />
        </div>
      )}
      <div className="flex items-center gap-1">
        {ffPersonaDetails && status !== "In progress" ? (
          <RevLink pathname="" search={`personaId=${id}`} text={name} />
        ) : (
          <span>{name}</span>
        )}
        {is_ai_generated && (
          <RevTooltip
            position="top"
            className="w-[335px]"
            content="This persona was automatically generated by our platform."
          >
            <FontAwesomeIcon
              icon={faStars}
              className="typography-body-md text-purple-600 ml-1"
            />
          </RevTooltip>
        )}
        {is_platform && (
          <RevTooltip
            position="top"
            className="w-[210px]"
            content="This persona is a platform persona."
          >
            <FontAwesomeIcon
              icon={faShieldCheck}
              className="typography-body-md text-monochrome-600 hover:text-primary-500 ml-1 transition-colors"
            />
          </RevTooltip>
        )}
      </div>
    </div>
  );
};

export const PersonasListPage: React.FC = () => {
  const {
    personaDetails: ffPersonaDetails,
    personaDelete: ffPersonaDelete,
    personaGenerate: ffPersonaGenerate,
    personaCreate: ffPersonaCreate,
    isTutorialPersonas: ffTutorialPersonas,
    hideTotalSize: ffHideTotalSize,
  } = useFlags();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("all");
  const [showPlatformOnly, setShowPlatformOnly] = useState(false);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<number[]>([]);
  const [isAddToGenerationModalOpen, setIsAddToGenerationModalOpen] =
    useState(false);
  const personasDataFromStore = useAppSelector(
    (state) => state.personas.personas,
  );
  const isLoadingList = useAppSelector((state) => state.personas.isLoadingList);
  const isLoadingGenerate = useAppSelector(
    (state) => state.personas.isLoadingGenerate,
  );
  const isDeleting = useAppSelector((state) => state.personas.isDeleting);

  const userProfile = useAppSelector((state) => state.profile.profile);
  const canManagePlatform = useMemo(() => {
    return (
      userProfile?.roles?.includes("INTERNAL_PLATFORM_CONTENT_MANAGER") ?? false
    );
  }, [userProfile]);

  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [personaToDelete, setPersonaToDelete] = useState<PersonaData | null>(
    null,
  );

  const isPolling = useRef(false);

  const personasInProgress = useMemo(() => {
    if (!personasDataFromStore) {
      return [];
    }
    return (personasDataFromStore as PersonaData[]).filter(
      (p: PersonaData) => p.status === "In progress",
    );
  }, [personasDataFromStore]);

  const numInProgress = personasInProgress.length;

  const hasEnrichingPersonas = useMemo(() => {
    if (!personasDataFromStore) {
      return false;
    }
    return (personasDataFromStore as PersonaData[]).some(isPersonaEnriching);
  }, [personasDataFromStore]);

  useEffect(() => {
    if (!personasDataFromStore) {
      dispatch(fetchPersonasData());
    }
  }, [dispatch, personasDataFromStore]);

  useEffect(() => {
    if (canManagePlatform) {
      dispatch(fetchPersonaGroups());
    }
  }, [dispatch, canManagePlatform]);

  useEffect(() => {
    const controller = new AbortController();

    if (numInProgress > 0 && !isPolling.current) {
      isPolling.current = true;
      dispatch(setIsLoadingGenerate(true));

      pollForPersonaGeneration({ signal: controller.signal })
        .then(() => {
          dispatch(
            displayAlert({
              type: "success",
              text: "AI personas generated.",
            }),
          );
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error("Polling failed:", err);
          }
        })
        .finally(() => {
          isPolling.current = false;
          dispatch(fetchPersonasData());
        });
    } else if (numInProgress === 0 && isLoadingGenerate) {
      dispatch(setIsLoadingGenerate(false));
    }

    return () => {
      controller.abort();
      isPolling.current = false;
    };
  }, [numInProgress, dispatch, isLoadingGenerate]);

  const dataToDisplay = useMemo(() => {
    const filterMap: FilterMapType = {
      all: () => true,
      user_created: (persona) => !persona.is_ai_generated,
      ai_generated: (persona) => persona.is_ai_generated,
    };

    if (!personasDataFromStore) {
      return [];
    }

    const filterFunction = filterMap[activeTab];
    let filteredData = personasDataFromStore.filter(filterFunction);

    if (showPlatformOnly) {
      filteredData = filteredData.filter((p) => p.is_platform);
    }

    return filteredData;
  }, [activeTab, personasDataFromStore, showPlatformOnly]);

  const handleTogglePersonaSelection = useCallback((personaId: number) => {
    setSelectedPersonaIds((prev) =>
      prev.includes(personaId)
        ? prev.filter((id) => id !== personaId)
        : [...prev, personaId],
    );
  }, []);

  const handleSelectAllPersonas = useCallback(() => {
    if (!dataToDisplay) return;
    if (selectedPersonaIds.length === dataToDisplay.length) {
      setSelectedPersonaIds([]);
    } else {
      setSelectedPersonaIds(dataToDisplay.map((p) => p.id));
    }
  }, [dataToDisplay, selectedPersonaIds.length]);

  const handleAddToGenerationSuccess = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (response: any, groupName: string, isPlatformGroup: boolean) => {
      setSelectedPersonaIds([]);
      if (response.skipped && response.skipped.length > 0) {
        const skippedCount = response.skipped[0].persona_ids.length;
        const reasonText = isPlatformGroup
          ? `because they aren't platform personas.`
          : `because they are platform personas.`;

        dispatch(
          displayAlert({
            type: "warning",
            text: `${skippedCount} personas weren't added to ${groupName} ${reasonText}`,
          }),
        );
      }
    },
    [dispatch],
  );

  const handleOpenDeleteDialog = useCallback((persona: PersonaData) => {
    setPersonaToDelete(persona);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setTimeout(() => setPersonaToDelete(null), 150);
  };

  const handleConfirmDelete = () => {
    if (!personaToDelete) return;

    dispatch(deletePersonaThunk(String(personaToDelete.id)))
      .unwrap()
      .catch((err) => {
        console.error("Failed to delete persona:", err);
      })
      .finally(() => {
        handleCloseDeleteDialog();
      });
  };

  const handleGenerateLoading = useCallback(() => {
    setIsGenerateDialogOpen(false);
    dispatch(fetchPersonasData());
  }, [dispatch, setIsGenerateDialogOpen]);

  const deleteDialogActions: RevDialogFooterBtnListType = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={handleCloseDeleteDialog}
    />,
    <RevButton
      key="delete"
      text="Delete Persona"
      variant="primary"
      buttonColor="error"
      handleClick={handleConfirmDelete}
      isLoading={isDeleting}
    />,
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<PersonaData, any>[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseColumns: ColumnDef<PersonaData, any>[] = [];

    if (canManagePlatform) {
      baseColumns.push(
        columnHelper.display({
          id: "select",
          header: () => {
            const allSelected =
              dataToDisplay.length > 0 &&
              selectedPersonaIds.length === dataToDisplay.length;
            return (
              <RevCheckbox
                checked={allSelected}
                handleChecked={handleSelectAllPersonas}
              />
            );
          },
          cell: (props) => {
            const isSelected = selectedPersonaIds.includes(
              props.row.original.id,
            );
            return (
              <RevCheckbox
                checked={isSelected}
                handleChecked={() =>
                  handleTogglePersonaSelection(props.row.original.id)
                }
              />
            );
          },
          size: 50,
        }),
      );
    }

    baseColumns.push(
      columnHelper.accessor("name", {
        id: "personaName",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Name
          </span>
        ),
        cell: (info) => <PersonaNameCell row={info.row} />,
        enableSorting: true,
        size: 300,
      }),
      columnHelper.accessor("affinity_strength", {
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">Fit</span>
        ),
        cell: (info) => {
          const strength = info.getValue();
          const fit =
            typeof strength === "string"
              ? getFitName(strength.toLowerCase())
              : null;

          return fit ? (
            <RevStatusBadge
              text={fit}
              variant="medium"
              color={getFitColor(fit.toLowerCase())}
            />
          ) : (
            ""
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor((row) => row.top_platforms, {
        id: "top_platforms",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Top Platforms
          </span>
        ),
        cell: (info) => {
          const platforms = info.getValue() as Platform[];

          if (!platforms || platforms.length === 0) {
            return "";
          }

          return (
            <div className="flex items-center gap-1">
              {platforms.map((platform) => {
                const icon = getPlatformIcon(platform.name.toLowerCase());
                return icon ? (
                  <FontAwesomeIcon
                    key={platform.id}
                    icon={icon}
                    className="text-monochrome-500 text-md"
                    title={platform.name}
                  />
                ) : (
                  <span
                    key={platform.id}
                    className="text-xs text-monochrome-500"
                    title={platform.name}
                  >
                    {platform.name}
                  </span>
                );
              })}
            </div>
          );
        },
        enableSorting: false,
        size: 180,
      }),
      columnHelper.accessor((row) => row.communities, {
        id: "communities",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Communities
          </span>
        ),
        cell: (info) => {
          const communities = info.getValue() as PersonaCommunity[] | undefined;

          if (!communities || communities.length === 0) {
            return "";
          }

          const topCommunityNames = [...communities]
            .sort((a, b) => b.community.size - a.community.size)
            .slice(0, 3)
            .map((communityObj) => communityObj.community.name)
            .join(", ");

          return (
            <div className="whitespace-normal break-words overflow-hidden line-clamp-2 typography-body-sm text-monochrome-950">
              {topCommunityNames}
            </div>
          );
        },
        enableSorting: true,
        size: 380,
      }),
      columnHelper.accessor((row) => row.interests, {
        id: "top3Interests",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Top 3 Interests
          </span>
        ),
        cell: (info) => {
          const interests = info.getValue();
          if (!interests || interests.length === 0) return "";

          const top3InterestsText = interests.slice(0, 3).join(", ");

          return (
            <RevTruncatedTextWithTooltip
              text={top3InterestsText}
              lines={2}
              className="whitespace-normal break-words overflow-hidden line-clamp-2 typography-body-sm text-monochrome-950"
            />
          );
        },
        enableSorting: true,
        size: 380,
      }),
      ...(!ffHideTotalSize
        ? [
            columnHelper.accessor("total_size", {
              header: () => (
                <span className="whitespace-nowrap typography-body-bold-xs">
                  Total Size
                </span>
              ),
              cell: (info) => {
                const size = info.getValue();
                return (
                  <div className="w-full text-right typography-body-sm text-monochrome-950">
                    {typeof size === "number"
                      ? fullFormatNumbers({
                          value: size,
                          noFormatNumber: true,
                          shortenValue: 1e3,
                        })
                      : ""}
                  </div>
                );
              },
              enableSorting: true,
              size: 120,
            }),
          ]
        : []),
      columnHelper.accessor("created_at", {
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Date Created
          </span>
        ),
        cell: (info) => formatDateToMonthDayYear(info.getValue() ?? ""),
        enableSorting: true,
        size: 150,
      }),
      columnHelper.accessor("updated_at", {
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Last Updated
          </span>
        ),
        cell: (info) => formatDateToMonthDayYear(info.getValue() ?? ""),
        enableSorting: true,
        size: 150,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Actions
          </span>
        ),
        cell: (props) => (
          <div className="flex items-center gap-1 justify-center">
            {ffPersonaDetails && (
              <RevTooltip
                position="top"
                className="w-[150px]"
                content="View / Manage Persona"
              >
                <RevIconButton
                  icon={faEye}
                  size="m"
                  iconSize="small"
                  variant="default"
                  handleClick={() =>
                    navigate(`/personas/?personaId=${props.row.original.id}`)
                  }
                  aria-label={`View / Manage ${props.row.original.name}`}
                />
              </RevTooltip>
            )}
            <RevTooltip
              position="top"
              className="w-[140px]"
              content="Discover Communities"
            >
              <RevIconButton
                icon={faTelescope}
                size="m"
                iconSize="small"
                variant="default"
                handleClick={() =>
                  navigate(
                    `/discover-communities?persona=${props.row.original.id}`,
                  )
                }
                aria-label={`Discover Communities for ${props.row.original.name}`}
              />
            </RevTooltip>
            {ffPersonaDelete && (
              <RevTooltip
                position="top"
                className="w-[100px]"
                content="Delete Persona"
              >
                <RevIconButton
                  icon={faTrash}
                  size="m"
                  iconSize="small"
                  variant="default"
                  handleClick={() => handleOpenDeleteDialog(props.row.original)}
                  aria-label={`Delete persona ${props.row.original.name}`}
                />
              </RevTooltip>
            )}
          </div>
        ),
        size: 120,
      }),
    );

    return baseColumns;
  }, [
    navigate,
    handleOpenDeleteDialog,
    ffPersonaDetails,
    ffPersonaDelete,
    ffHideTotalSize,
    canManagePlatform,
    selectedPersonaIds,
    dataToDisplay,
    handleSelectAllPersonas,
    handleTogglePersonaSelection,
  ]);

  if (isLoadingList) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  if (
    !isLoadingList &&
    personasDataFromStore &&
    personasDataFromStore.length === 0
  ) {
    return (
      <RevEmptyPage
        image={personaEmpty}
        title="No Personas"
        text="Build your first persona, and unlock the persona universe!"
        to=""
        search="?createPersona=true"
        textBtn="Create Persona"
      />
    );
  }

  return (
    <div>
      <RevActionsHeader
        title="Personas"
        headerClassName={`${isLoadingGenerate && `!mb-4`}`}
        videoBtn={ffTutorialPersonas}
      >
        <div className="flex gap-3">
          {/* Bulk action for users with INTERNAL_PLATFORM_CONTENT_MANAGER */}
          {canManagePlatform && selectedPersonaIds.length > 0 && (
            <RevButton
              text={`Add to Generation`}
              buttonSize="medium"
              variant="secondary"
              handleClick={() => setIsAddToGenerationModalOpen(true)}
            />
          )}
          {ffPersonaGenerate && (
            <RevButton
              text="Generate AI Persona"
              buttonSize="medium"
              iconLeft={solidFaStars}
              disabled={(personasDataFromStore ?? []).length >= 30}
              handleClick={() => setIsGenerateDialogOpen(true)}
            />
          )}
          {ffPersonaCreate && (
            <RevLink
              text="Create Persona"
              displayAs="button"
              buttonSize="medium"
              iconLeft={faPlus}
              pathname=""
              search="?createPersona=true"
            />
          )}
        </div>
      </RevActionsHeader>

      {/* Platform Filter for users with INTERNAL_PLATFORM_CONTENT_MANAGER */}
      {canManagePlatform && (
        <div className="mb-4 flex justify-end">
          <RevCheckbox
            label="Show only platform personas"
            checked={showPlatformOnly}
            handleChecked={() => setShowPlatformOnly(!showPlatformOnly)}
          />
        </div>
      )}

      {isLoadingGenerate && !isGenerateDialogOpen && (
        <div className="mb-3">
          <RevAlert
            data={{
              heading: "Generation of AI Persona(s) in-progress",
              text: "",
              type: "info",
              id: "generating-personas-id",
              show: true,
            }}
          />
        </div>
      )}
      {hasEnrichingPersonas && (
        <div className="mb-3">
          <RevAlert
            data={{
              heading: "Persona(s) are being enriched with data.",
              text: "",
              type: "info",
              id: "enriching-personas-id",
              show: true,
            }}
          />
        </div>
      )}
      <RevTabs
        data={personasListTabs}
        selected={activeTab}
        handleSelect={setActiveTab}
        tabId="persona-list"
        className="mb-5"
      />
      <RevTable<PersonaData>
        data={dataToDisplay}
        columns={columns}
        initialPageSize={10}
        enableGlobalFilter={false}
        enablePagination={true}
      />
      {personaToDelete && (
        <RevDialog
          isOpen={isDeleteDialogOpen}
          setIsOpen={setIsDeleteDialogOpen}
          title="Delete Persona"
          btnList={deleteDialogActions}
        >
          <p className="typography-body-sm text-monochrome-900">
            Are you sure you want to delete the persona {personaToDelete.name}?
            This action can't be undone.
          </p>
        </RevDialog>
      )}
      <GeneratePersonaDialog
        isOpen={isGenerateDialogOpen}
        setIsOpen={setIsGenerateDialogOpen}
        handleGenerateLoading={handleGenerateLoading}
      />
      {/* Add to Persona Group dialog for users with INTERNAL_PLATFORM_CONTENT_MANAGER */}
      {canManagePlatform && (
        <AddToPersonaGroupDialog
          isOpen={isAddToGenerationModalOpen}
          setIsOpen={setIsAddToGenerationModalOpen}
          selectedPersonaIds={selectedPersonaIds}
          onSuccess={handleAddToGenerationSuccess}
        />
      )}
    </div>
  );
};
